import React, { useState } from 'react';
import './StupidSystemDesigns.css';

const vendingMachineCode = String.raw`
#include <bits/stdc++.h>
using namespace std;

class Stock {
    int id;
    int quantity;
    int cost;
public:
    Stock(int id, int qty, int cost) : id(id), quantity(qty), cost(cost) {}
    
    int getId() { return id; }
    int getCost() { return cost; }
    
    void deductQuantity(int qty) { 
        quantity -= qty; 
    }
    
    void addQuantity(int qty) { 
        quantity += qty; 
    }
    
    bool hasQuantity(int qty) { 
        return quantity >= qty; 
    }
};
 
class CashMachine {
    int coinCount;
public:
    CashMachine() : coinCount(0) {} 

    bool hasCoins(int coins) { 
        return coinCount >= coins; 
    }      
    
    void deductCoins(int coins) { 
        coinCount -= coins;  
    }
    
    void addCoins(int coins) { 
        coinCount += coins; 
    }
    
    int getTotalBalance() {
        return coinCount;
    }
};

class VendingMachine {
public:
    enum class State { IDLE, IN_USE, MAINTENANCE };

private:
    unordered_map<int, Stock*> stockMap;
    CashMachine *cashMachine;
    State machineState;
    int addedCoins;  

public:
    VendingMachine() {
        machineState = State::IDLE;
        cashMachine = new CashMachine();
        addedCoins = 0;
    }

    ~VendingMachine() {
        for (auto const& pair : stockMap) {
            delete pair.second;
        }
        stockMap.clear();
        delete cashMachine;
    }

    void setState(State state) {
        machineState = state;
    }

    void insertCoins(int coins) {
        if (machineState != State::IDLE) {
            cout << "Machine is not ready for coins.\n";
            return;
        }
        setState(State::IN_USE);
        addedCoins = coins;
        cashMachine->addCoins(coins);
        cout << "Inserted " << coins << " coins.\n";
    }

    void addStock(int stockId, int qty, int cost = 4) {
        if (machineState != State::MAINTENANCE) {
            cout << "Cannot add stock. Machine not in MAINTENANCE state.\n";
            return;
        }
        if (!stockMap.count(stockId)) {
            Stock *stock = new Stock(stockId, qty, cost);
            stockMap[stockId] = stock;
        } else {
            stockMap[stockId]->addQuantity(qty);
        }
        cout << "Stock ID " << stockId << " updated. Added " << qty << " units.\n";
    }

    void selectStock(vector<pair<int,int>> selectedStocks) {
        if (machineState != State::IN_USE) {
            cout << "Please insert coins first.\n";
            return;
        }

        int currentTransactionCost = 0; 

        //validation pass
        for (auto &pairs : selectedStocks) {
            int stockId = pairs.first;
            int qty = pairs.second;
            
            if (!stockMap.count(stockId)) {
                cout << "Error: Stock " << stockId << " doesn't exist. Refunding...\n";
                returnCoins(addedCoins);
                return;
            }
            
            Stock *stock = stockMap[stockId];
            if (!stock->hasQuantity(qty)) {
                cout << "Error: Not enough quantity for stock " << stockId << ". Refunding...\n";
                returnCoins(addedCoins);
                return;
            }
            currentTransactionCost += (qty * stock->getCost());
        }

        // Funds Pass
        if (currentTransactionCost > addedCoins) {
            cout << "Error: Insufficient funds. Total is " << currentTransactionCost 
                 << " but you inserted " << addedCoins << ". Refunding...\n";
            returnCoins(addedCoins);
            return;
        }

        // Execution Pass
        for (auto &pairs : selectedStocks) {
            int stockId = pairs.first;
            int qty = pairs.second;
            stockMap[stockId]->deductQuantity(qty);
            cout << "Dispensed " << qty << " of Stock ID " << stockId << "\n";
        }

        // Finish and provide change
        cout << "Purchase successful.\n";
        returnCoins(addedCoins - currentTransactionCost);
    }

    void returnCoins(int coins) {
        if (coins > 0) {
            cout << "Returning " << coins << " coins.\n";
            cashMachine->deductCoins(coins);
        }
        
        addedCoins = 0; 
        setState(State::IDLE);
    }
    
    // Helper to check machine vault balance for testing
    void printMachineBalance() {
        cout << "[Admin] Machine Vault contains: " << cashMachine->getTotalBalance() << " coins.\n";
    }
};

//test it out with driver code

int main() {
    VendingMachine vm;

    cout << "--- 1. RESTOCKING PHASE ---\n";
    vm.addStock(101, 5);
    vm.setState(VendingMachine::State::MAINTENANCE);
    vm.addStock(101, 5, 10);
    vm.addStock(102, 3, 20);
    vm.setState(VendingMachine::State::IDLE);
    cout << "\n";

    cout << "--- 2. SUCCESSFUL PURCHASE ---\n";
    vm.insertCoins(50);
    vm.selectStock({{101, 2}, {102, 1}}); 
    vm.printMachineBalance();
    cout << "\n";

    cout << "--- 3. FAILED PURCHASE (INSUFFICIENT FUNDS) ---\n";
    vm.insertCoins(15);
    vm.selectStock({{102, 1}});
    vm.printMachineBalance();
    cout << "\n";

    cout << "--- 4. FAILED PURCHASE (OUT OF STOCK) ---\n";
    vm.insertCoins(100);
    vm.selectStock({{102, 5}});
    vm.printMachineBalance();
    cout << "\n";

    return 0;
}
`;

const stupidVendingMachineCode = String.raw`
#include <iostream>
#include <unordered_map>
#include <vector>

using namespace std;

class BadStock {
public:
    int id, quantity, cost;
    BadStock(int id, int qty, int cost) : id(id), quantity(qty), cost(cost) {}
};

class BadVendingMachine {
public:
    enum class State { IDLE, IN_USE };
    
    unordered_map<int, BadStock*> stockMap; 
    State machineState = State::IDLE;
    
    // FATAL FLAW 1: Persistent transaction variables
    int totalCostAccumulator = 0; 
    int currentCoins = 0;

    void insertCoins(int coins) {
        machineState = State::IN_USE;
        currentCoins += coins; // FATAL FLAW 2: Never gets reset if a user walks away
        cout << "[Machine] Received " << coins << " coins.\n";
    }

    void selectStock(vector<pair<int,int>> selections) {
        // FATAL FLAW 3: Non-atomic transactions (Deducting before validating)
        for(auto& pair : selections) {
            int id = pair.first;
            int qty = pair.second;

            if(!stockMap.count(id)) {
                cout << "[Error] Item doesn't exist!\n";
                return; // FATAL FLAW 4: Early return, leaving machine in IN_USE state and stealing money
            }

            // Deduct the stock IMMEDIATELY (What if they don't have enough money later?)
            stockMap[id]->quantity -= qty;
            
            // Accumulate the cost globally
            totalCostAccumulator += (qty * stockMap[id]->cost);
        }

        // FATAL FLAW 5: The "Debt" Bug
        if(currentCoins < totalCostAccumulator) {
            cout << "[Error] You need " << totalCostAccumulator << " coins, but only have " << currentCoins << ".\n";
            return; // Stock is already gone, money is stolen, state is stuck.
        }

        cout << "Dispensing items!\n";
        
        int change = currentCoins - totalCostAccumulator;
        cout << "Returning change: " << change << "\n";
        
        // FATAL FLAW 6: Forgetting to reset states after a SUCCESSFUL transaction!
        // currentCoins is not reset to 0
        // totalCostAccumulator is not reset to 0
        // machineState is left as IN_USE
    }
};

int main() {
    BadVendingMachine vm;
    vm.stockMap[1] = new BadStock(1, 10, 20); // 10 sodas, cost 20 each
    
    cout << "--- USER 1 WANTS A SODA ---\n";
    vm.insertCoins(20);
    vm.selectStock({{1, 1}}); // Success! Change: 0.

    cout << "\n--- USER 2 WANTS A SODA ---\n";
    vm.insertCoins(20); // User 2 inserts exactly the right amount.
    vm.selectStock({{1, 1}}); // Fails! Asks for 40 coins?

    cout << "\n--- CHECKING INVENTORY ---\n";
    cout << "Sodas left: " << vm.stockMap[1]->quantity << "\n"; // Wait, where did the sodas go?

    return 0;
}
`;

function VendingMachineDesign({ theme, onBackClick }) {
    const [copied, setCopied] = useState({ good: false, stupid: false });

    const handleCopyCode = async (codeKey, codeText) => {
        try {
            await navigator.clipboard.writeText(codeText);
            setCopied((previous) => ({ ...previous, [codeKey]: true }));
            window.setTimeout(() => {
                setCopied((previous) => ({ ...previous, [codeKey]: false }));
            }, 1800);
        } catch (error) {
            console.error('Copy failed:', error);
        }
    };

    return (
        <div className={`app ${theme}`}>
            <main className="system-design-page">
                <section className="system-designs-header-section">
                    <div className="system-designs-header">
                        <div className="system-designs-header-copy">
                            <h1>Vending Machine Design</h1>
                            <p className="system-designs-subtitle">
                                A small transaction system where a snack, a handful of coins, and a clean session lifecycle must all agree.
                            </p>
                        </div>
                        <button className="back-btn" onClick={onBackClick}>
                            Back to Designs
                        </button>
                    </div>
                </section>

                <section className="system-designs-content">
                    <div className="system-design-article">
                        <div className="design-section">
                            <h2>Functional Requirements</h2>
                            <ul>
                                <li>Maintain stock items with an ID, quantity, and price.</li>
                                <li>Allow a user to insert coins and begin one isolated purchase session.</li>
                                <li>Allow the user to select one or more stock items and quantities.</li>
                                <li>Return change after a successful purchase and dispense the selected stock.</li>
                                <li>Refund all inserted coins when an item is invalid, unavailable, or too expensive.</li>
                                <li>Reset the session to idle after both successful and failed transactions.</li>
                            </ul>
                        </div>

                        <div className="design-section">
                            <h2>Design</h2>
                            <ul>
                                <li><strong>Stock:</strong> owns item identity, price, and quantity validation.</li>
                                <li><strong>CashMachine:</strong> tracks the machine vault and accepts or returns coins.</li>
                                <li><strong>VendingMachine:</strong> owns the current session, coordinates validation, and controls state transitions.</li>
                                <li><strong>Validation pass:</strong> checks every selection and computes the total before any mutation.</li>
                                <li><strong>Execution pass:</strong> deducts stock only after the order and funds are known to be valid.</li>
                                <li><strong>Session cleanup:</strong> clears inserted coins and returns the machine to IDLE on every exit path.</li>
                            </ul>
                        </div>

                        <div className="design-section stupid-design-section">
                            <h2>Stupid Design</h2>
                            <ul>
                                <li><strong>The shared wallet:</strong> inserted coins live on the machine forever, so the next customer inherits them.</li>
                                <li><strong>Premature snack removal:</strong> stock is deducted before the complete order is validated.</li>
                                <li><strong>The growing debt:</strong> total cost is accumulated globally instead of per transaction.</li>
                                <li><strong>Refund optional:</strong> invalid selections and insufficient funds return neither money nor a clean state.</li>
                                <li><strong>Success is sticky:</strong> even a successful purchase leaves the machine in use with old transaction data.</li>
                            </ul>
                        </div>

                        <div className="design-section">
                            <h2>State Transitions and Errors</h2>
                            <ul>
                                <li><strong>IDLE {'->'} IN_USE:</strong> only when a positive coin amount is accepted.</li>
                                <li><strong>IN_USE {'->'} IDLE:</strong> after dispensing and returning change, or after a refund.</li>
                                <li>Reject coin insertion while another session is active or while the machine is unavailable.</li>
                                <li>Reject empty selections, unknown item IDs, non-positive quantities, and quantities above stock.</li>
                                <li>Keep stock and the vault unchanged when validation fails; only the inserted coins are refunded.</li>
                            </ul>
                        </div>

                        <div className="design-section">
                            <h2>Main Entities</h2>
                            <ul>
                                <li>VendingMachine: transaction coordinator and state owner.</li>
                                <li>Stock: product inventory record with price and quantity.</li>
                                <li>CashMachine: coin balance and refund/change operations.</li>
                                <li>Selection: an item ID and requested quantity for the current order.</li>
                            </ul>
                        </div>

                        <div className="design-section">
                            <div className="design-code-header">
                                <h2>Vending machine sample code:</h2>
                                <button type="button" className="copy-code-btn" onClick={() => handleCopyCode('good', vendingMachineCode)}>
                                    {copied.good ? 'Copied!' : 'Copy code'}
                                </button>
                            </div>
                            <pre className="design-code-block"><code>{vendingMachineCode}</code></pre>
                        </div>

                        <div className="design-section stupid-design-section">
                            <div className="design-code-header">
                                <h2>and also DONT BE STUUUUPPPPPPPIIIIDDDD!</h2>
                                <button type="button" className="copy-code-btn" onClick={() => handleCopyCode('stupid', stupidVendingMachineCode)}>
                                    {copied.stupid ? 'Copied!' : 'Copy code'}
                                </button>
                            </div>
                            <pre className="design-code-block"><code>{stupidVendingMachineCode}</code></pre>
                        </div>
                    </div>

                    <div className="system-designs-footer-note">
                        A vending machine is just a tiny payment system with snacks, state, and absolutely no patience for ambiguous ownership of 20 coins.
                    </div>

                    <button className="back-btn" onClick={onBackClick}>
                        Back to Designs
                    </button>
                </section>
            </main>

            <footer className="footer">
                <p>© 2026 StupidDesigns.com. All rights reserved. Not that anyone actually wants them.</p>
            </footer>
        </div>
    );
}

export default VendingMachineDesign;
